import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { UserContext } from "../UserContext";
import useNotification from "../../hooks/useNotification.hook";

export const NotificationContext = React.createContext({});

export const NotificationConsumer = NotificationContext.Consumer;

export function NotificationProvider({ children }) {
  const [activeCount, setActiveCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useContext(UserContext);
  const { getActiveCount } = useNotification();
  const hasFetched = useRef(false);

  // Fetch active count when user changes (login/logout)
  useEffect(() => {
    const fetchCount = async () => {
      if (!user) {
        setActiveCount(0);
        hasFetched.current = false;
        return;
      }

      // Avoid duplicate fetches
      if (hasFetched.current) return;
      hasFetched.current = true;

      try {
        setIsLoading(true);
        const count = await getActiveCount();
        setActiveCount(count);
      } catch {
        // Error handled silently - notification count is non-critical
      } finally {
        setIsLoading(false);
      }
    };

    fetchCount();
  }, [user, getActiveCount]);

  // Refresh active count - can be called manually
  const refreshActiveCount = useCallback(async () => {
    if (!user) {
      setActiveCount(0);
      return;
    }

    try {
      setIsLoading(true);
      const count = await getActiveCount();
      setActiveCount(count);
    } catch {
      // Error handled silently - notification count is non-critical
    } finally {
      setIsLoading(false);
    }
  }, [user, getActiveCount]);

  // Decrement count when notification is cleared
  const decrementActiveCount = useCallback(() => {
    setActiveCount(prev => Math.max(0, prev - 1));
  }, []);

  // Reset count when all are cleared
  const resetActiveCount = useCallback(() => {
    setActiveCount(0);
  }, []);

  const contextValue = useMemo(() => ({
    activeCount,
    isLoading,
    refreshActiveCount,
    decrementActiveCount,
    resetActiveCount
  }), [activeCount, isLoading, refreshActiveCount, decrementActiveCount, resetActiveCount]);

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
}

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default NotificationContext;
